/**
 * features/finance/SupplierStatements.tsx
 * ------------------------------------------------------------------
 * THE DOCUMENT THAT SETTLES A DISAGREEMENT.
 *
 * A supplier rings to say the school owes them P18,000. The school
 * believes it owes P12,000. Without a statement that argument is two
 * people reading their own spreadsheets at each other, and it is
 * usually settled by whoever is more insistent rather than whoever is
 * right — which is how a school pays an invoice twice.
 *
 * SO THIS IS NOT A LIST OF UNPAID BILLS. It is what the SUPPLIER's own
 * ledger should say: every charge and every payment, oldest first, with
 * a running balance. The payments are the half that resolves the
 * dispute, and a screen that showed only what was outstanding would
 * leave the school unable to prove it had already paid.
 *
 * NOTHING BELOW APPROVAL APPEARS. A draft or a rejected voucher is the
 * school's internal business and was never an obligation to anybody.
 * Putting one on a document a supplier may be shown would be telling
 * them about money they were never promised.
 *
 * THE BANKING IS SHOWN BESIDE IT, with whether it has been verified.
 * Somebody reading a statement is often about to pay it, and this is the
 * moment the account number is worth a second look.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  FileText, ShieldCheck, ShieldAlert, Building2, Mail, Phone, Printer,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import {
  VOUCHER_COLLECTIONS, supplierStatement, VOUCHER_STATUS_LABEL,
  type Voucher,
} from '@/lib/vouchers';
import {
  SUPPLIER_COLLECTION, isBankVerified, type Supplier,
} from '@/lib/suppliers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, ExportBar, glassFieldClass,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SupplierStatements() {
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [search, setSearch] = useState('');

  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (snap) => setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (snap) => setVouchers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);

  /**
   * The picker lists registered suppliers AND anybody who has been paid
   * without being registered. Leaving the latter out would mean the one
   * payee most likely to be queried — an ad-hoc reimbursement nobody
   * recorded properly — is the one with no statement.
   */
  const parties = useMemo(() => {
    const out: { key: string; name: string; supplier?: Supplier }[] =
      (suppliers ?? []).map((s) => ({ key: s.id, name: s.name, supplier: s }));

    const seen = new Set(out.map((p) => p.name.trim().toLowerCase()));
    for (const v of vouchers ?? []) {
      if (v.supplierId) continue;
      const name = (v.payeeName || '').trim();
      if (!name || seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      out.push({ key: `name:${name.toLowerCase()}`, name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [suppliers, vouchers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? parties.filter((p) => p.name.toLowerCase().includes(q)) : parties;
  }, [parties, search]);

  const party = parties.find((p) => p.key === selected);

  const statement = useMemo(() => {
    if (!party || !vouchers) return null;
    return supplierStatement(
      vouchers,
      party.supplier ? { id: party.supplier.id, name: party.name } : { name: party.name },
    );
  }, [party, vouchers]);

  function exportCsv() {
    if (!statement || !party) return;
    const head = ['Date', 'Voucher', 'Description', 'Charged', 'Paid', 'Balance'];
    const lines = statement.lines.map((l) => [
      l.date,
      l.voucher.voucherNo,
      `"${(l.voucher.narration || '').replace(/"/g, '""')}"`,
      l.charged ? l.charged.toFixed(2) : '',
      l.paid ? l.paid.toFixed(2) : '',
      l.balance.toFixed(2),
    ].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement-${party.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (suppliers === null || vouchers === null) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  const verified = party?.supplier ? isBankVerified(party.supplier) : false;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={FileText}
        tone="navy"
        title="Supplier Statements"
        subtitle="Every charge and every payment, the way the supplier's own ledger reads it"
        actions={statement && statement.lines.length > 0
          ? (
            <>
              <ExportBar onCsv={exportCsv} />
              <button type="button" onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-gray-200 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
                <Printer size={15} /> Print
              </button>
            </>
          )
          : undefined}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <SoftCard pad="p-0" className="h-fit">
          <PanelHeader icon={Building2} title="Who" tone="navy"
            subtitle={`${parties.length} payee${parties.length === 1 ? '' : 's'}`} />
          <div className="p-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a supplier…"
              className={glassFieldClass()}
            />
          </div>
          <div className="max-h-[520px] overflow-y-auto pb-2">
            {filtered.length === 0 && (
              <p className="px-4 py-3 text-sm text-gray-500">No payee matches that.</p>
            )}
            {filtered.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelected(p.key)}
                className={`w-full text-left px-4 py-2 text-sm border-l-4 transition-colors ${
                  selected === p.key
                    ? 'border-primary bg-primary/5 font-medium text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/50'
                }`}
              >
                <span className="truncate block">{p.name}</span>
                {!p.supplier && (
                  <span className="text-[10px] text-gray-400">not in the register</span>
                )}
              </button>
            ))}
          </div>
        </SoftCard>

        <div>
          {!party ? (
            <SoftCard>
              <p className="text-sm text-gray-500 text-center py-8">
                Choose a payee to see their statement.
              </p>
            </SoftCard>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatChip
                  tone={statement && statement.closing > 0 ? 'navy' : 'emerald'}
                  value={money(statement?.closing ?? 0)}
                  label="Balance outstanding"
                />
                <StatChip tone="slate"
                  value={String(statement?.lines.filter((l) => l.charged > 0).length ?? 0)}
                  label="Bills on this statement" />
                <StatChip tone="slate"
                  value={money(statement?.lines.reduce((a, l) => a + l.paid, 0) ?? 0)}
                  label="Paid to date" />
              </div>

              {party.supplier && (
                <SoftCard className="mb-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-900 dark:text-white">{party.supplier.name}</h3>
                      <div className="mt-1 space-y-0.5 text-[13px] text-gray-600 dark:text-gray-300">
                        {party.supplier.contactEmail && (
                          <p className="flex items-center gap-1.5">
                            <Mail size={13} className="text-gray-400" />{party.supplier.contactEmail}
                          </p>
                        )}
                        {party.supplier.contactPhone && (
                          <p className="flex items-center gap-1.5">
                            <Phone size={13} className="text-gray-400" />{party.supplier.contactPhone}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Read at the moment somebody is most likely to be about
                        to pay this. An unverified account is not an accusation;
                        it is a reason to make one phone call. */}
                    {party.supplier.bank && (
                      <div className={`rounded-lg border-l-4 px-3 py-2 text-[13px] ${
                        verified
                          ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                          : 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
                      }`}>
                        <p className="flex items-center gap-1.5 font-medium">
                          {verified
                            ? <><ShieldCheck size={14} className="text-emerald-600" /> Banking verified</>
                            : <><ShieldAlert size={14} className="text-amber-600" /> Banking not verified</>}
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 mt-0.5">
                          {party.supplier.bank.bankName} · {party.supplier.bank.accountNumber}
                        </p>
                      </div>
                    )}
                  </div>
                </SoftCard>
              )}

              <SoftCard pad="p-0">
                <PanelHeader icon={FileText} title="Statement" tone="navy"
                  subtitle="Oldest first, with the balance after each line" />

                {!statement || statement.lines.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">
                    Nothing has been approved for this payee, so there is no statement yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                          <th className="py-2 pl-4 pr-2 font-semibold">Date</th>
                          <th className="px-2 font-semibold">Voucher</th>
                          <th className="px-2 font-semibold">Description</th>
                          <th className="px-2 font-semibold text-right">Charged</th>
                          <th className="px-2 font-semibold text-right">Paid</th>
                          <th className="px-2 pr-4 font-semibold text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.lines.map((l, i) => (
                          <tr key={`${l.voucher.id}-${i}`}
                            className="border-b border-gray-50 dark:border-gray-800">
                            <td className="py-2 pl-4 pr-2 tabular-nums text-gray-600 dark:text-gray-300">
                              {l.date}
                            </td>
                            <td className="px-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                              {l.voucher.voucherNo}
                            </td>
                            <td className="px-2 text-gray-700 dark:text-gray-200">
                              <span className="truncate block max-w-[320px]">
                                {l.paid > 0 ? 'Payment' : l.voucher.narration}
                              </span>
                              {l.paid > 0 && l.voucher.bankRef && (
                                <span className="text-[11px] text-gray-400">ref {l.voucher.bankRef}</span>
                              )}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {l.charged ? money(l.charged) : ''}
                            </td>
                            <td className="px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                              {l.paid ? money(l.paid) : ''}
                            </td>
                            <td className="px-2 pr-4 text-right tabular-nums font-medium">
                              {money(l.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
                          <td className="py-2.5 pl-4 pr-2" colSpan={5}>
                            Balance outstanding
                          </td>
                          <td className="px-2 pr-4 text-right tabular-nums">
                            {money(statement.closing)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </SoftCard>

              {/* What is still moving through the chain. Not part of the
                  statement — none of it is owed yet — but the first thing
                  asked once the balance is read. */}
              <InFlight vouchers={vouchers} party={party} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InFlight({
  vouchers, party,
}: { vouchers: Voucher[]; party: { name: string; supplier?: Supplier } }) {
  const pending = vouchers.filter((v) => {
    const mine = party.supplier
      ? v.supplierId === party.supplier.id
      : !v.supplierId && (v.payeeName || '').trim().toLowerCase() === party.name.trim().toLowerCase();
    return mine && ['draft', 'submitted', 'checked'].includes(v.currentStatus);
  });

  if (pending.length === 0) return null;

  return (
    <SoftCard className="mt-4">
      <p className="text-[13px] text-gray-500 mb-2">
        Not on the statement — {pending.length} voucher{pending.length === 1 ? '' : 's'} still
        being processed, and not yet owed to this payee.
      </p>
      <div className="space-y-1">
        {pending.map((v) => (
          <div key={v.id} className="flex items-center justify-between text-[13px]">
            <span className="font-mono text-xs text-gray-500">{v.voucherNo}</span>
            <span className="text-gray-600 dark:text-gray-300 truncate px-3 flex-1">
              {v.narration}
            </span>
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {VOUCHER_STATUS_LABEL[v.currentStatus]}
            </span>
            <span className="ml-3 tabular-nums text-gray-500">{money(v.total)}</span>
          </div>
        ))}
      </div>
    </SoftCard>
  );
}
